import { casaBool, casaTexto } from "@/lib/filtros/texto";
import type { Usuario } from "@/lib/usuarios";

/**
 * CLAVES_USUARIOS son los parámetros que admite el listado GLOBAL de usuarios (#247).
 * `es_admin` volvió con #285: se llama como el campo que el API publica, porque el contrato
 * exige los MISMOS nombres en el API y en la URL.
 */
export const CLAVES_USUARIOS = [
  "obra",
  "rol",
  "activo",
  "es_admin",
  "invitacion",
  "sso",
  "q",
] as const;

/** FiltrosUsuarios es el objeto de filtros del listado de usuarios tal como viaja en la URL. */
export type FiltrosUsuarios = Partial<Record<(typeof CLAVES_USUARIOS)[number], string>>;

/**
 * ESTADOS_INVITACION son los cuatro valores del filtro `invitacion` (#247), con su etiqueta
 * para el desplegable. Son EXACTAMENTE los que deriva `estadoInvitacion` de los dos campos
 * publicados; el servidor aplica la misma derivación en SQL.
 */
export const ESTADOS_INVITACION = [
  { valor: "pendiente", etiqueta: "Invitado" },
  { valor: "caducada", etiqueta: "Invitación caducada" },
  { valor: "revocada", etiqueta: "Invitación revocada" },
  { valor: "ninguna", etiqueta: "Sin invitación pendiente" },
] as const;

/**
 * estadoInvitacion resuelve el estado de invitación de un usuario a partir de los dos
 * campos publicados (#239 D12/E1): `invitacion_pendiente` manda —mientras siga pendiente la
 * cuenta no entra— y la invitación VIVA solo matiza el porqué. Revocar NO cierra el caso:
 * el usuario sigue pendiente y sin invitación viva.
 */
export function estadoInvitacion(u: Usuario): "pendiente" | "caducada" | "revocada" | "ninguna" {
  if (!u.invitacion_pendiente) {
    return "ninguna";
  }
  if (!u.invitacion) {
    return "revocada";
  }
  return u.invitacion.estado;
}

/**
 * filtrarUsuarios aplica los filtros en el navegador. El listado de usuarios NO se persiste
 * offline (la allowlist de la caché lo excluye a propósito), así que esta función existe por
 * la regla de equivalencia (D2/R7) y para el filtrado optimista mientras llega la respuesta;
 * la autoridad sigue siendo el servidor. Tabla de casos compartida en
 * backend/internal/usermgmt/filtros_integration_test.go (TestFiltrosUsuariosHTTP).
 */
export function filtrarUsuarios(filas: Usuario[], filtros: FiltrosUsuarios): Usuario[] {
  return filas.filter((u) => {
    if (filtros.obra !== undefined && filtros.obra !== "") {
      const enObra = u.obras.filter((o) => String(o.id_obra) === filtros.obra);
      if (enObra.length === 0) {
        return false;
      }
      // El rol es POR OBRA: solo cuenta el que tiene EN esa obra.
      if (
        filtros.rol !== undefined &&
        filtros.rol !== "" &&
        !enObra.some((o) => o.rol === filtros.rol)
      ) {
        return false;
      }
    }
    if (!casaBool(filtros.activo, u.activo)) {
      return false;
    }
    if (!casaBool(filtros.es_admin, u.es_admin)) {
      return false;
    }
    if (!casaBool(filtros.sso, u.entra_upn != null && u.entra_upn !== "")) {
      return false;
    }
    if (
      filtros.invitacion !== undefined &&
      filtros.invitacion !== "" &&
      estadoInvitacion(u) !== filtros.invitacion
    ) {
      return false;
    }
    return casaTexto(
      filtros.q,
      u.nombre_usuario,
      u.persona.nombre,
      u.persona.apellidos,
      u.persona.email,
    );
  });
}
